import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import HowToVoteOutlinedIcon from "@mui/icons-material/HowToVoteOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";

/** Label above the create-event type chip picker. */
export const EVENT_TYPE_PICKER_LABEL = "Event type *";

/** Screen reader label for the type chip radiogroup. */
export const EVENT_TYPE_PICKER_ACCESSIBILITY_LABEL = "Event type, required";

export const CANONICAL_EVENT_TYPE_ORDER = ["community", "volunteer", "campaign"];

/** Legacy DB slugs that map to a canonical create-event chip. */
export const LEGACY_EVENT_TYPE_TO_CANONICAL = {
  town_hall: "community",
  community_forum: "community",
  policy_workshop: "community",
  community_event: "community",
  election: "campaign",
  campaign_event: "campaign",
  volunteer_event: "volunteer",
};

export function canonicalEventTypeName(typeName) {
  if (!typeName) return "";
  return LEGACY_EVENT_TYPE_TO_CANONICAL[typeName] || typeName;
}

/** One chip per canonical bucket, even if the API still returns legacy rows. */
export function filterCanonicalEventTypes(eventTypes = []) {
  if (!eventTypes.length) return [];
  const byName = new Map(eventTypes.map((type) => [type.name, type]));
  const usedIds = new Set();
  const result = [];

  for (const canonical of CANONICAL_EVENT_TYPE_ORDER) {
    let match = byName.get(canonical);
    if (!match) {
      match = eventTypes.find(
        (type) =>
          canonicalEventTypeName(type.name) === canonical && !usedIds.has(type.id)
      );
    }
    if (match && !usedIds.has(match.id)) {
      usedIds.add(match.id);
      result.push(match);
    }
  }

  return result;
}

/** Map a selected type id to the visible chip in the same canonical bucket. */
export function resolveEventTypeChipSelection(eventTypes = [], visibleTypes = [], selectedId) {
  if (!visibleTypes.length) return selectedId;
  if (visibleTypes.some((type) => String(type.id) === String(selectedId))) {
    return selectedId;
  }

  const selected = eventTypes.find((type) => String(type.id) === String(selectedId));
  if (!selected) return visibleTypes[0]?.id ?? selectedId;

  const canonical = canonicalEventTypeName(selected.name);
  const match = visibleTypes.find(
    (type) => canonicalEventTypeName(type.name) === canonical
  );
  return match ? match.id : (visibleTypes[0]?.id ?? selectedId);
}

/** Short labels for the create-event type chip grid. */
export const EVENT_TYPE_TILE_LABELS = {
  community: "Community",
  community_event: "Community",
  volunteer: "Volunteer",
  volunteer_event: "Volunteer",
  campaign: "Campaign",
  campaign_event: "Campaign",
};

export const EVENT_TYPE_DESCRIPTIONS = {
  community:
    "Meetups, discussions, town halls, forums, and workshops — anything where people come together around civic issues.",
  community_event:
    "Meetups, discussions, town halls, forums, and workshops — anything where people come together around civic issues.",
  volunteer: "Recruit volunteers for service, outreach, or organizing.",
  volunteer_event: "Recruit volunteers for service, outreach, or organizing.",
  campaign: "Run canvassing, rallies, election events, and political organizing.",
  campaign_event: "Run canvassing, rallies, election events, and political organizing.",
};

const EVENT_TYPE_ICON_COMPONENTS = {
  community: GroupsOutlinedIcon,
  community_event: GroupsOutlinedIcon,
  volunteer: VolunteerActivismOutlinedIcon,
  volunteer_event: VolunteerActivismOutlinedIcon,
  campaign: HowToVoteOutlinedIcon,
  campaign_event: HowToVoteOutlinedIcon,
};

export function getEventTypeTileLabel(typeName, displayName) {
  const canonical = canonicalEventTypeName(typeName);
  return (
    EVENT_TYPE_TILE_LABELS[canonical] ||
    EVENT_TYPE_TILE_LABELS[typeName] ||
    displayName ||
    typeName ||
    "Event"
  );
}

export function getEventTypeDescription(typeName) {
  const canonical = canonicalEventTypeName(typeName);
  return EVENT_TYPE_DESCRIPTIONS[canonical] || EVENT_TYPE_DESCRIPTIONS[typeName] || "";
}

export function getEventTypeIcon(typeName) {
  const canonical = canonicalEventTypeName(typeName);
  const Icon =
    EVENT_TYPE_ICON_COMPONENTS[canonical] ||
    EVENT_TYPE_ICON_COMPONENTS[typeName] ||
    EventOutlinedIcon;
  return <Icon />;
}
